/**
 * Calendar primitives shared by DateTimePicker (single instant) and
 * DateRangePicker (from → to). The value format is the same one the native
 * inputs use — `YYYY-MM-DDTHH:mm` (datetime) or `YYYY-MM-DD` (date) — and is
 * zone-agnostic: a literal wall clock the host interprets (UTC for the
 * analysis range / anchors, broker-local for order expiry).
 */

export type Mode = "datetime" | "date";

export interface Parts {
  year: number;
  month: number; // 0-11
  day: number;
  hour: number;
  minute: number;
}

export const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
export const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
export const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export const pad = (n: number) => String(n).padStart(2, "0");

export function parse(value: string): Parts | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(value);
  if (!m) return null;
  return {
    year: +m[1],
    month: +m[2] - 1,
    day: +m[3],
    hour: m[4] != null ? +m[4] : 0,
    minute: m[5] != null ? +m[5] : 0,
  };
}

export function format(p: Parts, mode: Mode): string {
  const d = `${p.year}-${pad(p.month + 1)}-${pad(p.day)}`;
  return mode === "datetime" ? `${d}T${pad(p.hour)}:${pad(p.minute)}` : d;
}

/** Human label: "5 Jul 2026 · 22:00". */
export function display(p: Parts | null, mode: Mode, placeholder: string): string {
  if (!p) return placeholder;
  const base = `${p.day} ${MONTHS_SHORT[p.month]} ${p.year}`;
  return mode === "datetime" ? `${base} · ${pad(p.hour)}:${pad(p.minute)}` : base;
}

/** Sortable day key — compares calendar days, ignoring the time. */
export const dayKey = (p: { year: number; month: number; day: number }) =>
  p.year * 10000 + p.month * 100 + p.day;

export const dateKey = (d: Date) =>
  d.getFullYear() * 10000 + d.getMonth() * 100 + d.getDate();

/** The 42 Monday-first day cells of the month grid for `view`. */
export function monthCells(view: { y: number; m: number }): Date[] {
  const start = new Date(view.y, view.m, 1);
  start.setDate(1 - ((start.getDay() + 6) % 7));
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

export function addMonths(v: { y: number; m: number }, delta: number) {
  const m = v.m + delta;
  return { y: v.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 };
}
